import React from 'react';
import {
	Box,
	Button,
	Grid,
	CardContent,
	CircularProgress,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { paths } from '@/app/routes';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import { showToast, hideToast } from '@/features/toast/toastSlice';
import { useCreateRelationshipCompanyMutation } from '@/features/relationshipCompanies/relationshipCompaniesApi';
import validationSchema from './validationSchema';
import { RelationshipsIcon } from '@/components/icons/RelationshipsIcon';
import { companyProfileProperties, toRelationshipCompanyInitialValues } from './fields';
import CompanyTextField from './Fields/CompanyTextField';
import CompanyDetailsFields from './Fields/CompanyDetailsFields';
import PrimaryContactFields from './Fields/PrimaryContactFields';

const CreateRelationshipCompanyForm = ({ onClose }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const [createRelationshipCompany, { isLoading: isCreatingRelationshipCompany }] =
		useCreateRelationshipCompanyMutation();

	const icon = (
		<RelationshipsIcon
			{...(theme.palette.mode === 'dark' && { bgColor: 'transparent' })}
		/>
	);

	return (
		<Formik
			initialValues={{
				...toRelationshipCompanyInitialValues(),
				isExistingRelationshipContact: false,
				existingRelationshipContact: null,
				firstName: '',
				lastName: '',
				primaryEmail: '',
				mobile: '',
				relationshipType: 'other',
			}}
			validationSchema={validationSchema}
			validateOnBlur
			validateOnChange={false}
			onSubmit={async (values, { resetForm, setSubmitting }) => {
				const toastId = Math.random();
				dispatch(
					showToast({
						message: 'Creating company...',
						autohide: true,
						duration: 6000,
						loading: true,
						id: toastId,
					}),
				);

				try {
					const relationshipContactId =
						values.isExistingRelationshipContact &&
						values.existingRelationshipContact?.id;
					const result = await createRelationshipCompany({
						...companyProfileProperties(values),
						...(relationshipContactId
							? { relationshipContactId }
							: {
									contact: {
										firstName: values.firstName.trim(),
										lastName: values.lastName.trim(),
										email: values.primaryEmail.trim(),
										mobile: values.mobile.trim(),
										relationshipType: values.relationshipType || 'other',
									},
								}),
					}).unwrap();
					dispatch(hideToast(toastId));
					dispatch(
						showToast({
							message: 'Company created!',
							autohide: true,
							duration: 6000,
							...(result?.id && {
								action: {
									path: `${paths.companies}/${result.id}`,
									label: 'GO TO COMPANY',
								},
							}),
						}),
					);
					resetForm();
					onClose?.();
				} catch (error) {
					dispatch(hideToast(toastId));
					dispatch(
						showToast({
							message:
								error?.data?.message ??
								error?.message ??
								'Unable to create company.',
							error: true,
						}),
					);
				} finally {
					setSubmitting(false);
				}
			}}
		>
			{({ isSubmitting, handleSubmit, validateForm }) => {
				const disabled = isSubmitting || isCreatingRelationshipCompany;
				return (
					<Form
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						<FolioDrawerHeader title='Create new company' icon={icon} />
						<Box
							sx={{
								flex: 1,
								overflowY: 'auto',
							}}
						>
							<Box>
								<FormikErrorScroll isSubmitting={isSubmitting} />
								<CardContent>
									<Typography variant='body1' mb='1.15em'>
										Fill out the following form to create your new company.
									</Typography>
									<Stack spacing={2}>
										<Grid container spacing={2}>
											<Grid size={{ xs: 12, sm: 6 }}>
												<CompanyTextField
													name='name'
													label='Company name'
													required
													disabled={disabled}
												/>
											</Grid>
										</Grid>
										<CompanyDetailsFields disabled={disabled} />
										<PrimaryContactFields disabled={disabled} />
									</Stack>
								</CardContent>
							</Box>
						</Box>
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							<Button
								color='primary'
								type='button'
								variant='contained'
								disabled={disabled}
								onClick={() => {
									handleSubmit();
									setTimeout(() => validateForm(), 100);
								}}
							>
								{disabled ? (
									<CircularProgress
										size='2em'
										sx={{ color: theme.palette.primary.contrastText }}
									/>
								) : (
									'Submit'
								)}
							</Button>
						</FolioDrawerFooter>
					</Form>
				);
			}}
		</Formik>
	);
};

export default CreateRelationshipCompanyForm;
